import Paper from "@mui/material/Paper";
import axios from "axios";
import moment from "moment-timezone";

import { Button, Chip, CircularProgress } from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridToolbar,
} from "@mui/x-data-grid";
import { useSignalEffect, useSignals } from "@preact/signals-react/runtime";
import React, { useState } from "react";
import { toast } from "react-toastify";
import { MAX_TABLE_ROWS } from "../data/constants";
import { app } from "../signals/AppSignal";
import { parsePlan } from "../utils/StringUtil";

interface IUsers {
  id: number;
  dateAdded: string;
  userName: string;
  accountSlug: string;
  userEmail: string;
  hasToken: boolean;
  planId: string | null;
  isAdmin: boolean;
  isViewOnly: boolean;
  isTrial: boolean;
  credit: number;
  usedCredits: number;
}

const RevokeActionCell = ({
  params,
  getUsersList,
}: {
  params: any;
  getUsersList: () => Promise<void>;
}) => {
  const [isProcessing, setIsProcessing] = React.useState(false);

  const handleRevoke = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const { accountId, userId } = params.row;

    if (
      !window.confirm(
        `Are you sure you want to revoke the token for User ID: ${userId}?`,
      )
    ) {
      return;
    }

    setIsProcessing(true);
    try {
      // Dynamic URI using your signal
      const subDomain = app.value === "qrit" ? "qr" : "stp";
      const revokeUri = `https://${subDomain}.getfluidapps.dev/api/v2/user/revoke`;

      const response = await axios({
        method: "delete",
        url: revokeUri,
        headers: { "Content-Type": "application/json" },
        data: { accountId, userId },
      });

      if (response.data.status) {
        toast.success("Token revoked successfully!");
        await getUsersList();
      } else {
        toast.error(response.data.message || "Failed to revoke token.");
      }
    } catch (error: any) {
      console.error("Revoke Error:", error);
      toast.error(error.response?.data?.message || "Failed to revoke token.");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Button
      variant="outlined"
      color="error"
      size="small"
      onClick={handleRevoke}
      disabled={isProcessing}
      startIcon={
        isProcessing ? <CircularProgress size={16} color="inherit" /> : null
      }
      sx={{ textTransform: "none", fontWeight: 600, minWidth: "120px" }}
    >
      {isProcessing ? "Revoking..." : "Revoke Token"}
    </Button>
  );
};

const Users = () => {
  useSignals();

  const columns: GridColDef[] = [
    {
      field: "id",
      type: "number",
      headerAlign: "right",
      headerName: "#",
      width: 50,
    },
    {
      field: "dateAdded",
      type: "string",
      headerAlign: "left",
      headerName: "Date Added",
      sortable: true,
      width: 200,
    },
    {
      field: "accountId",
      type: "string",
      headerAlign: "left",
      headerName: "Account ID",
      sortable: true,
      width: 100,
    },
    {
      field: "accountSlug",
      type: "string",
      headerAlign: "left",
      headerName: "Slug",
      sortable: true,
      width: 150,
    },
    {
      field: "userId",
      type: "string",
      headerAlign: "left",
      headerName: " User ID",
      sortable: true,
      width: 100,
    },
    {
      field: "userName",
      type: "string",
      headerAlign: "left",
      headerName: "User Name",
      sortable: true,
      width: 150,
    },
    {
      field: "userEmail",
      type: "string",
      headerAlign: "left",
      headerName: "Email",
      sortable: true,
      width: 200,
    },
    {
      field: "hasToken",
      type: "boolean",
      headerAlign: "center",
      headerName: "Has Token",
      sortable: true,
      width: 100,
    },
    {
      field: "isAdmin",
      type: "boolean",
      headerAlign: "center",
      headerName: "Is Admin",
      sortable: true,
      width: 100,
    },
    {
      field: "isViewOnly",
      type: "boolean",
      headerAlign: "center",
      headerName: "Is View Only",
      sortable: true,
      width: 120,
    },
    {
      field: "isTrial",
      type: "boolean",
      headerAlign: "center",
      headerName: "Trial",
      sortable: true,
      width: 80,
    },
    {
      field: "planId",
      type: "string",
      headerAlign: "left",
      headerName: "Plan Id",
      sortable: true,
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={params.value === "free" ? "success" : "warning"}
          sx={{ width: "100%", justifyContent: "center" }}
        />
      ),
      cellClassName: (params) => getPlanClass(params.value),
    },
    {
      field: "usedCredits",
      type: "number",
      headerAlign: "center",
      headerName: "Used",
      sortable: true,
      width: 80,
      renderCell: (params) => (
        <div className="w-100 text-center align-center">
          {params.value !== null ? params.value : "0"}
        </div>
      ),
    },
    {
      field: "balance",
      type: "number",
      headerAlign: "center",
      headerName: "Balance",
      sortable: true,
      width: 80,
      renderCell: (params) => (
        <div className="w-100 text-center align-center">
          {params.value !== null ? params.value : "0"}
        </div>
      ),
    },
    {
      field: "credit",
      type: "number",
      headerAlign: "center",
      headerName: "Credit",
      sortable: true,
      width: 80,
      renderCell: (params) => (
        <div className="w-100 text-center align-center">
          {params.value !== -1 ? params.value : "∞"}
        </div>
      ),
    },

    {
      field: "actions",
      headerName: "Actions",
      type: "actions",
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <RevokeActionCell params={params} getUsersList={getUsersList} />
      ),
    },
  ];

  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [users, setUsers] = useState<IUsers[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [pagnition, setPagnation] = useState<any>({});

  const [paginationModel, setPaginationModel] = useState<{
    page: number;
    pageSize: number;
  }>({ page: 0, pageSize: 50 });

  const rowCount = users.length || 0;
  const pageCount = Math.ceil(rowCount / paginationModel.pageSize);
  const startIndex = paginationModel.page * paginationModel.pageSize + 1;
  const endIndex = Math.min(
    (paginationModel.page + 1) * paginationModel.pageSize,
    rowCount,
  );

  const getTablePage = (totalPages: number) => {
    const pages: React.ReactNode[] = [];
    const currentPage = pagnition.currentPage;

    // Controls how many pages to show before and after the current page
    const neighbors = 2;

    for (let i = 1; i <= totalPages; i++) {
      // Always show the first page, the last page, and pages immediately surrounding the current page
      if (
        i === 1 ||
        i === totalPages ||
        (i >= currentPage - neighbors && i <= currentPage + neighbors)
      ) {
        const isCurrent = currentPage === i;
        pages.push(
          <li key={i} className="page-item">
            <button
              disabled={isCurrent}
              className={
                isCurrent
                  ? "page-link text-white fw-bold active"
                  : "page-link text-dark"
              }
              onClick={() => getUsersList(i)}
            >
              {i}
            </button>
          </li>,
        );
      }
      // Add Ellipsis if we are skipping chunks of pages
      else if (
        i === currentPage - neighbors - 1 ||
        i === currentPage + neighbors + 1
      ) {
        pages.push(
          <li key={`ellipsis-${i}`} className="page-item disabled">
            <span className="page-link text-muted bg-light border-0">...</span>
          </li>,
        );
      }
    }

    return pages;
  };

  const handlePageSizeChange = (event: { target: { value: string } }) => {
    setPaginationModel((prev) => ({
      ...prev,
      pageSize: parseInt(event.target.value, 10),
      page: 0, // Reset to first page if pageSize changes
    }));
  };

  const handleNext = () => {
    setPaginationModel((prev) => ({
      ...prev,
      page: Math.min(prev.page + 1, pageCount - 1),
    }));
  };

  const handlePrev = () => {
    setPaginationModel((prev) => ({
      ...prev,
      page: Math.max(prev.page - 1, 0),
    }));
  };

  const getPlanClass = (status: string) => {
    switch (status) {
      case "free":
        return "status-completed";
      case "basic":
        return "status-pending";
      case "pro":
        return "status-cancelled";
      case "ultimate":
        return "status-cancelled";
      default:
        return "";
    }
  };

  const convertUTCToLocal = (utcDateStr: string) => {
    return moment
      .utc(utcDateStr) // Parse as UTC
      .tz(moment.tz.guess())
      .format("MMMM D, YYYY, HH:mm:ss");
  };

  const getUsersList = async (page: number = 1) => {
    setLoading(true);
    setUsers([]);
    axios
      .get(
        `/api/v1/customers/${app.value}/users?limit=${MAX_TABLE_ROWS}&page=${page}`,
      )
      .then((response) => {
        // handle success
        console.log(response.data);
        setPagnation(response.data.pagination);
        setPaginationModel({
          page: response.data.pagination.currentPage,
          pageSize: response.data.pagination.pageSize,
        });

        let usersList: IUsers[] = response.data.data || [];
        usersList = usersList.map((list, index) => ({
          ...list,
          id: index + 1,
          balance: list.credit - list.usedCredits,
          planId: parsePlan(app.value, list.planId),
          dateAdded: convertUTCToLocal(list.dateAdded),
        }));
        setUsers(usersList);
      })
      .catch((error) => {
        // handle error
        console.log(error);
        setUsers([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getAppValue = () => {
    switch (app.value) {
      case "eam":
      case "qrit":
        setColumnVisibilityModel({});
        break;
      case "smarttd":
      case "smartwn":
        setColumnVisibilityModel({
          usedCredits: false,
          balance: false,
          credit: false,
        });
        break;
      default:
        setColumnVisibilityModel({});
        break;
    }
  };

  useSignalEffect(() => {
    getAppValue();
    getUsersList();
  });

  return (
    <div className="row mt-2">
      <div className="col-12">
        <div className="card">
          <div className="card-header">
            <div className="d-lg-flex">
              <div>
                <h5 className="mb-0">Users</h5>
              </div>
              <div className="ms-auto my-auto mt-lg-0 mt-4">
                <div className="ms-auto my-auto">
                  <button
                    type="button"
                    className="btn btn-icon-only btn-rounded btn-secondary mb-0 ms-2 btn-sm d-flex align-items-center justify-content-center ms-auto"
                    data-bs-toggle="tooltip"
                    data-bs-placement="bottom"
                    title="Refresh"
                    onClick={() => getUsersList()}
                  >
                    <i className="material-symbols-rounded text-md">sync</i>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="card-body">
            <div className="mb-4 row align-items-center compact-controls">
              <div className="col-auto d-flex align-items-center gap-2">
                <select
                  className="form-select form-select-sm select-list"
                  onChange={handlePageSizeChange}
                  value={paginationModel.pageSize}
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="30">30</option>
                  <option value="40">40</option>
                  <option value="50">50</option>
                </select>
                <span>entries per page</span>
              </div>

              <div className="col d-flex align-items-center gap-2">
                <button
                  className="m-0 btn btn-sm btn-outline-info"
                  onClick={handlePrev}
                  disabled={paginationModel.page === 0}
                >
                  Previous
                </button>

                <button
                  className="m-0 btn btn-sm btn-outline-info"
                  onClick={handleNext}
                  disabled={paginationModel.page >= pageCount - 1}
                >
                  Next
                </button>

                <div
                  className="d-flex align-items-center"
                  style={{ height: "100%" }}
                >
                  <span style={{ lineHeight: 1 }}>
                    Page {startIndex}–{endIndex} of {rowCount}
                  </span>
                </div>
              </div>

              <div className="col d-flex align-items-center justify-content-end">
                <nav aria-label="Table navigation">
                  <ul className="pagination pagination-sm mb-0 align-items-center">
                    {/* Previous Button */}
                    <li className="page-item">
                      <button
                        className="page-link text-dark"
                        disabled={pagnition?.currentPage === 1}
                        onClick={() => {
                          const prev = pagnition?.currentPage - 1;
                          if (prev >= 1) getUsersList(prev);
                        }}
                      >
                        <span aria-hidden="true">&laquo;</span>
                      </button>
                    </li>

                    {/* Dynamic Page List */}
                    {getTablePage(pagnition?.totalPages || 0)}

                    {/* Next Button */}
                    <li className="page-item">
                      <button
                        className="page-link text-dark"
                        disabled={
                          pagnition?.currentPage === pagnition?.totalPages
                        }
                        onClick={() => {
                          const next = pagnition?.currentPage + 1;
                          if (next <= pagnition?.totalPages) getUsersList(next);
                        }}
                      >
                        <span aria-hidden="true">&raquo;</span>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>

            <Paper sx={{ height: "550px", width: "100%" }}>
              <DataGrid
                loading={loading}
                rows={users}
                columns={columns}
                hideFooter={true}
                slots={{ toolbar: GridToolbar }}
                initialState={{ pagination: { paginationModel } }}
                paginationModel={paginationModel}
                onPaginationModelChange={(newModel) =>
                  setPaginationModel(newModel)
                }
                pageSizeOptions={[10, 20, 30, 40, 50]}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={(newModel: any) =>
                  setColumnVisibilityModel(newModel)
                }
                localeText={{ noRowsLabel: "No data available to display" }}
                disableRowSelectionOnClick
                sx={{ border: 0 }}
              />
            </Paper>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Users;
